// Frase de sofá no carregamento de filme e série ("Pipoca quase pronta").
//
// FOLHA com estado próprio: os ticks re-renderizam só este nó, nunca a tela do player
// (SeriesPlayerScreen / VodPlayerScreen / MobilePlayer), que é pesada. O pai monta
// `<LoadingPhrase key={idDoConteúdo} surface=… />` dentro do overlay de `loading`:
//   - o overlay desmonta no `playing` e no erro → o relógio morre junto;
//   - nova tentativa AUTOMÁTICA mantém `loading` → o nó fica montado → não zera;
//   - "Tentar novamente" passa por `error` → remonta → zera;
//   - outro episódio/filme muda a `key` → remonta → zera.
//
// A linha é SEMPRE reservada (mesmo vazia), pro spinner não pular quando a 1ª frase
// entra. Aos 60 s a frase dá lugar ao AVISO de demora (título + como sair), que cresce
// pra BAIXO a partir do topo da linha reservada: o spinner também não pula.
//
// Acessibilidade: a frase é `aria-hidden` (frase trocando a cada 4 s num anúncio
// seria ruído; o anúncio do carregamento é o aria-label do Spinner). O aviso, ao
// contrário, é informação útil e é anunciado UMA vez: a região `role="status"` existe
// VAZIA desde a montagem e recebe o texto uma única vez (aos 60 s); depois não muda.

import { useTranslation } from '@app-iptv/i18n'
import { useEffect, useState } from 'react'

import {
  LOADING_STALLED_KEYS,
  createLoadPhraseDeck,
  nextPhraseTick,
  type PhraseSurface,
} from '../lib/loading-phrases'

import styles from './LoadingPhrase.module.css'

interface LoadingPhraseProps {
  /** Superfície: decide o baralho (sem as frases de controle/celular no toque) e a
   *  dica do aviso de demora (botão voltar do controle × voltar do toque). */
  surface: PhraseSurface
  /** Classe extra (o MobilePlayer usa a própria escala de fonte). */
  className?: string | undefined
}

/** `t()` devolve a própria chave quando ela falta no locale: nunca mostrar a crua. */
function present(t: (k: string) => string, key: string): string {
  const v = t(key)
  return v === key ? '' : v
}

export function LoadingPhrase({ surface, className }: LoadingPhraseProps) {
  const { t } = useTranslation()
  const [phraseKey, setPhraseKey] = useState<string | null>(null)
  const [stalled, setStalled] = useState(false)

  useEffect(() => {
    const deck = createLoadPhraseDeck(surface)
    let elapsed = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    // Um único setTimeout encadeado (nunca setInterval): cada passo agenda o próximo,
    // e depois do aviso não agenda nada.
    const schedule = (): void => {
      const tick = nextPhraseTick(elapsed)
      if (tick === null) return
      timer = setTimeout(() => {
        timer = null
        elapsed = tick.at
        if (tick.kind === 'show') {
          setPhraseKey(deck.next())
        } else {
          setPhraseKey(null)
          setStalled(true)
        }
        schedule()
      }, tick.at - elapsed)
    }
    schedule()

    return () => {
      if (timer !== null) clearTimeout(timer)
    }
    // A superfície é decidida no boot e não muda na sessão (a dep só documenta).
  }, [surface])

  const text = phraseKey === null ? '' : present(t, phraseKey)
  const title = stalled ? present(t, LOADING_STALLED_KEYS.title) : ''
  const hint = stalled
    ? present(t, surface === 'touch' ? LOADING_STALLED_KEYS.hintTouch : LOADING_STALLED_KEYS.hintTv)
    : ''

  return (
    <div className={className ? `${styles.line} ${className}` : styles.line} data-loading-phrase="">
      {text !== '' && (
        // `key` = a chave da frase: cada frase nova é um nó novo, então o fade-in roda
        // só nela (sem fade-out da anterior).
        <span key={phraseKey ?? ''} className={styles.text} aria-hidden="true" data-phrase="">
          {text}
        </span>
      )}
      <div className={styles.stalled} role="status" aria-atomic="true" data-loading-stalled={stalled ? '' : undefined}>
        {title !== '' && <span className={styles.stalledTitle}>{title}</span>}
        {hint !== '' && <span className={styles.stalledHint}>{hint}</span>}
      </div>
    </div>
  )
}
